import React, {
    Fragment, useCallback, useEffect, useState,
  } from 'react';
  import { Link, RouteComponentProps } from 'react-router-dom';
  import {
    CircularProgress,
    Grid,
    MenuItem,
    Typography,
    Toolbar,
    FormControlLabel,
    Checkbox
  } from '@material-ui/core';
  import Button from '@material-ui/core/Button';
  import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
  import { Autocomplete } from '@material-ui/lab';
  import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
  import { Paper, TextField } from '@material-ui/core';
  import PropertySearch from '../../components/PropertySearch/PropertySearch';
  import { useSnackbar } from 'notistack';
  import { gql } from 'apollo-boost';
  import useStyles from './styles';
  import {
    DEVICE_BY_ID,
    GET_ADC_LOCK_DEVICES,
    UPDATE_DEVICE,
  } from '../../graphql/queries/Devices'
  import CustomTextField from '../../components/CustomTextField/CustomTextField';
  import {
    beautifyErrors,
    ValidationErrors,
  } from '../../utils/helpers';
  import CustomButton from '../../components/CustomButton';
  import BuilderSearch from '../../components/BulderSearch/BuilderSearch';
  import Builder from '../../types/Builder';
  import InputDevice from '../../types/inputTypes/Device';
  import { BaseProperty as Property } from '../../types/Property';
  import useProperty from '../../hooks/useProperty';
  import { deviceCreateSchema } from '../Device/DeviceForm/DeviceValidation';
  import HubTypeSelect from '../../components/HubTypeSelect';
  import DeviceHubIcon from '@material-ui/icons/DeviceHub';
  import { goToZwaveTool } from '../../services/zwave-tool';
  import DeviceActivationLogsTable from './DeviceActivationLogsTable';

  type Props = {
    loading?: boolean;
  } & RouteComponentProps;

  type AdcLockDevice = {
    deviceId: number;
    deviceName: string;
    statusCodes: number[];
    currentlyMapped: boolean;
  };

  const DeviceDetails: React.FC<Props> = props => {
    const {history} = props;
    const deviceId = history.location.pathname.split('/')[2]
    const classes = useStyles();
    const [validationErrors, setValidationErrors] = useState<ValidationErrors<InputDevice>>({});
    const [device, setDevice] = useState<InputDevice>(new InputDevice());
    const [adcDeviceInputValue, setAdcDeviceInputValue] = useState<string>('');
    const [buildersId, setBuildersId] = useState<{ name: string, id?: number }[]>([]);
    const { property } = useProperty(device.property_id);
    const [HubAccountId, setHubAccountId] = useState<{ id: string, username: string }[]>([]);

    const { enqueueSnackbar } = useSnackbar();

    const { data, loading } = useQuery<{ getHubById: InputDevice }>(
        DEVICE_BY_ID,
        {
          variables: { serial_number: deviceId },
          fetchPolicy: 'network-only',
        },
    );

    useEffect(() => {
      if (data) {
        setDevice(data.getHubById)
        setAdcDeviceInputValue(data.getHubById.adc_device_id == null
          ? 'All locks'
          : String(data.getHubById.adc_device_id));
      }
    }, [data])

    const [updateDevice] = useMutation<{ updateDevice: InputDevice }>(
      UPDATE_DEVICE,
      { errorPolicy: 'all' }
    );

    const [loadAdcLockDevices, {
      data: adcLockDevicesData,
      loading: adcLockDevicesLoading,
      error: adcLockDevicesError,
    }] = useLazyQuery<{ getAdcLockDevices: AdcLockDevice[] }>(GET_ADC_LOCK_DEVICES, {
      fetchPolicy: 'network-only',
    });

    useEffect(() => {
      const hub = data?.getHubById;
      if (hub?.hub_type === 'ADC' && hub.id) {
        loadAdcLockDevices({ variables: { hubId: hub.id } });
      }
    }, [data, loadAdcLockDevices]);

    const GET_BUILDERIDS = gql`
    query {
      builderIds {
        builder_id,
        name
      }
    }
    `;


    const GET_HUBACCOUNT_IDS = gql`
    query {
      hubAccountIds {
        id,
        username,
      }
    }
    `;

    const { data: builderData } = useQuery<{ builderIds: Array<{ name: string, id?: number }> }>(
      GET_BUILDERIDS,
    );


    const { data: hubAccountData } = useQuery<{ hubAccountIds: Array<{ id: string, username: string }> }>(
      GET_HUBACCOUNT_IDS,
    );

    useEffect(() => {
      if (hubAccountData) {
        setHubAccountId(hubAccountData.hubAccountIds);
      }
    }, [hubAccountData, HubAccountId]);

    const handleDeviceChange = useCallback(
      ({ target: { name, value, checked, type } }: React.ChangeEvent<HTMLInputElement>) => {
        if (name) {
          setDevice((prevDevice: InputDevice) => ({
            ...prevDevice,
            [name]: type === 'checkbox' ? checked : value,
          }));
        }
      },
      [],
    );

    const handleDeviceSave = () => {
    try{
      const { logs, ...updatedDevice } = device;
      const uDevice = deviceCreateSchema.validateSync(updatedDevice, { abortEarly: false })
      updateDevice({
        variables: {
          device: {
            ...uDevice,
          },
    }}).then(() => {
      enqueueSnackbar('Success', { variant: 'success' });
    });
  } catch (errors) {
    enqueueSnackbar('Error', { variant: 'error' });
    setValidationErrors(beautifyErrors<InputDevice>(errors));
  }
  }

    const handleBuilderChange = (builder: Builder) => {
      setDevice((device: InputDevice) => ({
        ...device,
        property_id: 0,
        builder_id: builder.builder_id,
      }));
    }

    const handlePropertyChange = (property: Property) => {
      setDevice((device: InputDevice) => ({
        ...device,
        builder_id: property.builder_id ?? 0,
        property_id: property.property_id,
      })
      )
    }

    const handleControlHub = () => {
      history.push(`/ezlo-tool?deviceId=${deviceId}`);
    }

    const handleControlHubByZwaveInstall = () => {
      goToZwaveTool(device.hub_account_id, deviceId);
    }

    useEffect(() => {
      if (builderData) {
        setBuildersId(builderData.builderIds);
      }
    }, [builderData, buildersId]);

    const renderEzloFields = () => {
      return (
        <Fragment>
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                margin="normal"
                id="serial_number"
                name="serial_number"
                label="Serial number"
                value={device.serial_number}
                onChange={handleDeviceChange}
                helperText={validationErrors.serial_number || ''}
              />
            </Grid>
        </Fragment>
      );
    }

    const renderEzloWifiFields = () => {
      //TODO: temporarily use ezlo fields until documentation for Ezlo Wifi is provided
      return (
        <Fragment>
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                margin="normal"
                id="serial_number"
                name="serial_number"
                label="Serial number"
                value={device.serial_number}
                onChange={handleDeviceChange}
                helperText={validationErrors.serial_number || ''}
              />
            </Grid>
        </Fragment>
      );
    }

    const renderPeplinkFields = () => {
      //TODO: temporarily use ezlo fields until documentation for Peplink is provided
      return (
        <Fragment>
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                margin="normal"
                id="serial_number"
                name="serial_number"
                label="Serial number"
                value={device.serial_number}
                onChange={handleDeviceChange}
                helperText={validationErrors.serial_number || ''}
              />
            </Grid>
        </Fragment>
      );
    }

    const renderSeamFields = () => {
      return (
        <Fragment>
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                margin="normal"
                id="serial_number"
                name="serial_number"
                label="Device Id"
                value={device.serial_number}
                onChange={handleDeviceChange}
                helperText={validationErrors.serial_number || ''}
              />
            </Grid>
        </Fragment>
      );
    }

    const renderADCFields = () => {
      const adcLockDevices = adcLockDevicesData?.getAdcLockDevices || [];
      const storedDeviceId =
        device.adc_device_id === null || device.adc_device_id === undefined
          ? null
          : Number(device.adc_device_id);
      const storedDeviceIsMissing = storedDeviceId !== null && !adcLockDevices.some(lock => lock.deviceId === storedDeviceId);
      const adcDeviceOptions = ['none', ...adcLockDevices.map(lock => String(lock.deviceId))];

      return (
        <Fragment>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                id="serial_number"
                name="serial_number"
                label="Serial number"
                value={device.serial_number}
                onChange={handleDeviceChange}
                helperText={validationErrors.serial_number || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="normal"
                id="adc_customer_id"
                name="adc_customer_id"
                label="ADC Customer ID"
                value={device.adc_customer_id}
                onChange={handleDeviceChange}
                helperText={validationErrors.adc_customer_id || ''}
              />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={adcDeviceOptions}
              value={storedDeviceId === null ? 'none' : String(storedDeviceId)}
              inputValue={adcDeviceInputValue}
              getOptionLabel={option => option === 'none' ? 'All locks' : option}
              noOptionsText="Enter a DeviceID"
              loading={adcLockDevicesLoading}
              onInputChange={(_event, value, reason) => {
                if (reason !== 'input') return;
                if (value !== '' && !/^\d+$/.test(value)) return;

                setAdcDeviceInputValue(value);
                if (value === '') {
                  setDevice(currentDevice => ({ ...currentDevice, adc_device_id: null }));
                } else {
                  setDevice(currentDevice => ({ ...currentDevice, adc_device_id: Number(value) }));
                }
              }}
              onChange={(_event: React.ChangeEvent<{}>, value: string | null) => {
                if (value === null || value === 'none') {
                  setAdcDeviceInputValue('All locks');
                  setDevice(currentDevice => ({ ...currentDevice, adc_device_id: null }));
                  return;
                }

                const selectedDeviceId = Number(value);
                if (Number.isInteger(selectedDeviceId)) {
                  setAdcDeviceInputValue(String(selectedDeviceId));
                  setDevice(currentDevice => ({  ...currentDevice, adc_device_id: selectedDeviceId }));
                }
              }}
              renderOption={option => {
                if (option === 'none') return 'All locks';

                const lock = adcLockDevices.find(item => String(item.deviceId) === option);
                if (!lock) return `DeviceID ${option}`;

                return `${lock.deviceName} — DeviceID ${lock.deviceId}${
                  lock.deviceId === storedDeviceId ? ' (current)' : ''
                }`;
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  margin="normal"
                  id="adc_device_id"
                  name="adc_device_id"
                  label="ADC Device Id"
                  helperText={validationErrors.adc_device_id || ''}
                  error={!!validationErrors.adc_device_id}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {adcLockDevicesLoading
                          ? <CircularProgress color="inherit" size={20} />
                          : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
            />
            {storedDeviceIsMissing && !adcLockDevicesError && !adcLockDevicesLoading && (
              <Typography color="error" variant="caption">
                DeviceID
                {' '}
                {storedDeviceId}
                {' '}
                was not found in Alarm.com.
              </Typography>
            )}
          </Grid>
        </Fragment>
      );
    }

    const renderKwiksetFields = () => {
      return (
        <Fragment>
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                margin="normal"
                id="serial_number"
                name="serial_number"
                label="Serial number"
                value={device.serial_number}
                onChange={handleDeviceChange}
                helperText={validationErrors.serial_number || ''}
              />
            </Grid>
        </Fragment>
      );
    }

    const renderSchlageFields = () => {
      return (
        <Fragment>
          <Grid item xs={12} sm={12}>
            <TextField
              fullWidth
              margin="normal"
              id="serial_number"
              name="serial_number"
              label="Serial Number"
              value={device.serial_number}
              onChange={handleDeviceChange}
              helperText={validationErrors.serial_number || ''}
            />
          </Grid>
        </Fragment>
      );
    }

    return(
      <Fragment>
      <div className={classes.actionsBar}>
        <div className={classes.actionButtonsWrapper}>

          <Button
            variant="contained"
            className={classes.backToButton}
            component={Link}
            to="/devices"
          >
            <ArrowLeftIcon />
            Devices
          </Button>
        </div>
      </div>
      <div className={classes.contentWrapper}>
        <Grid item xs={12} sm={12}>
          <Paper className={classes.paper}>
          <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <HubTypeSelect
              margin="normal"
              fullWidth
              id="hub_type"
              label="Hub Type"
              name="hub_type"
              select
              value={device.hub_type}
              onChange={handleDeviceChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
            <TextField
              margin="normal"
              fullWidth
              select
              label="Hub Account"
              name="hub_account_id"
              onChange={handleDeviceChange}
              value={device.hub_account_id}
              helperText={validationErrors.hub_account_id}
              >
                {HubAccountId.sort((a, b) => a.username.localeCompare(b.username)).map(HubAcc => (
                  <MenuItem
                    key={HubAcc.id}
                    value={HubAcc.id}
                  >
                    {HubAcc.username}
                  </MenuItem>
                ))}
             </TextField>
            </Grid>
            {(device.hub_type === 'Ezlo Secure') && renderEzloFields()}
            {(device.hub_type === 'Ezlo Wifi') && renderEzloWifiFields()}
            {(device.hub_type === 'Peplink') && renderPeplinkFields()}
            {(device.hub_type === 'Seam') && renderSeamFields()}
            {(device.hub_type === 'ADC') && renderADCFields()}
            {(device.hub_type === 'Kwikset') && renderKwiksetFields()}
            {(device.hub_type === 'Schlage') && renderSchlageFields()}
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                margin="normal"
                id="imei"
                name="imei"
                label="IMEI"
                value={device.imei}
                onChange={handleDeviceChange}
                helperText={validationErrors.imei || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <BuilderSearch
                margin="normal"
                onBuilderChange={handleBuilderChange}
                label="Builder"
                initialValue={device.builder_id}
                helperText={validationErrors.builder_id}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PropertySearch
                style={{ marginTop: '0' }}
                margin="normal"
                fullWidth
                onPropertyChange={handlePropertyChange}
                helperText={validationErrors.property_id}
                //@ts-ignore
                initialValue={property?.property_id ? property : undefined}
                builderId = {device.builder_id}
             />
            </Grid>
            {
              device.hub_type.toLowerCase() === 'adc' && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        color="secondary"
                        name="delay_animation"
                        checked={Boolean(device.delay_animation)}
                        onChange={handleDeviceChange}
                      />
                    )}
                    label="Delay Animation"
                    labelPlacement="end"
                  />
                </Grid>
              )
            }
            <Grid item xs={12} sm={6}>
                    <CustomTextField
                      margin="normal"
                      fullWidth
                      multiline
                      rows={4}
                      rowsMax={8}
                      label="Comments"
                      name="comments"
                      value={device.comments || ''}
                      onChange={handleDeviceChange}
                    />
              </Grid>
              <Grid item xs={12} sm={6}>
                    <CustomTextField
                      margin="normal"
                      fullWidth
                      multiline
                      rows={4}
                      rowsMax={8}
                      label="Descriprion"
                      name="description"
                      value={device.description || ''}
                      onChange={handleDeviceChange}
                    />
              </Grid>
            <Grid container spacing={4} justify="center" style={{ marginTop: '10px', marginBottom: '10px' }}>
                <Grid item>
                    <CustomButton variant="orange" onClick={handleDeviceSave}>
                      Update device
                    </CustomButton>
                </Grid>
              { device.hub_type === 'Ezlo Secure' && (
                  <>
                    <Grid item>
                      <CustomButton variant="orange" onClick={() => handleControlHub()}>
                        <DeviceHubIcon /> Control Hub
                      </CustomButton>
                    </Grid>
                    <Grid item>
                      <CustomButton variant="orange" onClick={() => handleControlHubByZwaveInstall()}>
                        <DeviceHubIcon /> Control Hub By Zwave Install
                      </CustomButton>
                    </Grid>
                  </>
              )}
              </Grid>
              </Grid>
          </Paper>
      </Grid>
      </div>
        <Grid container direction="column" className={classes.historyWrapper}>
          <Toolbar>
            <Typography variant="h5">Hardware History</Typography>
          </Toolbar>
          <DeviceActivationLogsTable logs={device.logs} isLoading={loading} />
        </Grid>

      </Fragment>
    )
  }

  export default React.memo(DeviceDetails);
